import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from 'react-query'
import { Button, ConfirmDialog, Notification } from '../../../components'
import { createExtemporaneousAppointment } from '../../../services'
import { translateAppointmentError } from '../../../utils/inboundAppointments'

const T = 'replenishments.inbound_appointments.extemporaneous'

// Registers a walk-in arrival: the appointment is born checked_in, dated
// warehouse-local today, no franja, no capacity held. Creation by an
// authorized ops user IS the approval — there is no pending state (the board's
// class filter doubles as the review view).
export const ExtemporaneousAction = ({ replenishmentId }) => {
  const { i18n } = useTranslation()
  const queryClient = useQueryClient()
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [showSuccess, setShowSuccess] = useState(false)
  const [errorMsg, setErrorMsg] = useState(null)

  const mutation = useMutation(() => createExtemporaneousAppointment(replenishmentId), {
    onSuccess: () => {
      setConfirmOpen(false)
      setShowSuccess(true)
      // The refetched replenishment carries the checked_in appointment,
      // so the section swaps this action for the unload panel.
      queryClient.invalidateQueries(['replenishment_inbound_appointment', replenishmentId])
      // Creation also flips the host replenishment WITHOUT_SCHEDULING →
      // REQUESTED server-side — refresh the monoliths' own caches too
      // (eng-review D23). Prefix match covers the paginated key's params.
      queryClient.invalidateQueries(['replenishment', replenishmentId])
      queryClient.invalidateQueries(['box_replenishment', replenishmentId])
    },
    onError: error => {
      setConfirmOpen(false)
      setErrorMsg(translateAppointmentError(i18n, error))
    },
  })

  return (
    <section className="flex-1 bg-white p-5" data-testid="extemporaneous-action">
      <Notification show={showSuccess} setShow={setShowSuccess} type="success" title={i18n.t(`${T}.success`)} />
      <Button
        type="secondary"
        onClick={() => {
          setErrorMsg(null)
          setConfirmOpen(true)
        }}
      >
        {i18n.t(`${T}.button`)}
      </Button>
      {errorMsg && <div className="font-bold text-red-500 text-sm mt-2">{errorMsg}</div>}
      <ConfirmDialog
        open={confirmOpen}
        setOpen={setConfirmOpen}
        title={i18n.t(`${T}.confirm_title`)}
        description={i18n.t(`${T}.confirm_description`)}
        confirmLabel={i18n.t(`${T}.confirm`)}
        cancelLabel={i18n.t(`${T}.cancel`)}
        onConfirm={() => mutation.mutate()}
        onCancel={() => setConfirmOpen(false)}
        loading={mutation.isLoading}
      />
    </section>
  )
}
